import type { ReactNode } from "react";
import { useState } from "react";
import { navigate } from "@reach/router";
import { useTranslation } from "react-i18next";
import { useFirebaseGame } from "src/hooks/useFirebaseGame";
import { useAuth } from "src/contexts/AuthContext";
import { initialState, kiswahiliStartState } from "src/reducer";
import RadioButton from "../RadioButton";
import RadioButtonGroup from "../RadioButtonGroup";
import { KISWAHILI, KUJIFUNZA } from "src/core/baogame";
import type { GameType, Side } from "src/core/types";
import type { Settings } from "../../core/types";
import type { Theme } from "src/theme";

type Props = {
  path: string;
};

const containerStyle = ({ colors, gridUnit }: Theme) => ({
  position: "fixed" as const,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  flexDirection: "column" as const,
  background: colors.grey95,
  overflowY: "auto" as const,
  "@media (min-width: 480px)": {
    justifyContent: "center"
  }
});

const cardStyle = ({ colors, gridUnit }: Theme) => ({
  background: "white",
  color: colors.textPrimary,
  padding: `${gridUnit * 6}px ${gridUnit * 5}px`,
  width: "100%",
  minHeight: "100vh",
  boxSizing: "border-box" as const,
  paddingTop: gridUnit * 18,
  "@media (min-width: 480px)": {
    padding: gridUnit * 10,
    paddingTop: gridUnit * 10,
    borderRadius: gridUnit * 3,
    boxShadow: `0 ${gridUnit}px ${gridUnit * 1.5}px rgba(0,0,0,0.1)`,
    minWidth: "400px",
    maxWidth: "500px",
    minHeight: "auto",
    width: "auto"
  }
});

const sectionStyle = ({ gridUnit }: Theme) => ({
  marginBottom: gridUnit * 6
});

const infoBoxStyle = ({ colors, gridUnit }: Theme) => ({
  marginTop: gridUnit * 6,
  padding: gridUnit * 4,
  background: colors.grey95,
  borderRadius: gridUnit * 1.5,
  fontSize: gridUnit * 3.5,
  color: colors.grey1
});

const gameTypes = [
  { labelKey: "createGame.gameType.kujifunza", value: KUJIFUNZA },
  { labelKey: "createGame.gameType.kiswahili", value: KISWAHILI }
];

const startingSides = [
  { labelKey: "createGame.youStart", value: 0 },
  { labelKey: "createGame.opponentStarts", value: 1 }
];

const CreateGame = (props: Props): ReactNode => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [gameType, setGameType] = useState<GameType>(KUJIFUNZA);
  const [startingSide, setStartingSide] = useState<Side>(0);
  const [creating, setCreating] = useState<boolean>(false);

  const { createGame } = useFirebaseGame(null);

  // Wait for authentication
  if (!user) {
    return (
      <div css={containerStyle}>
        <div css={(theme) => ({ color: theme.colors.textPrimary })}>{t("createGame.authenticating")}</div>
      </div>
    );
  }

  const handleCreateGame = async () => {
    if (!user) return;

    setCreating(true);

    const startingGameState = gameType === KISWAHILI ? kiswahiliStartState : initialState.currentGameState;
    const settings: Settings = {
      playbackSpeed: 400,
      expertLevel: "expert",
      startingSide,
      showHints: true
    };

    const newGameId = await createGame(user.uid, startingGameState, settings, gameType);

    if (newGameId) {
      // Navigate after a short delay to allow Firebase to sync
      setTimeout(() => {
        navigate(`/multiplayer/game/${newGameId}`);
      }, 500);
    } else {
      setCreating(false);
      alert(t("createGame.failed"));
    }
  };

  return (
    <div css={containerStyle}>
      <div css={cardStyle}>
        <h1
          css={(theme) => ({
            marginTop: 0,
            marginBottom: theme.gridUnit * 5,
            textAlign: "center",
            fontSize: theme.gridUnit * 6,
            "@media (min-width: 480px)": {
              marginBottom: theme.gridUnit * 7.5,
              fontSize: "2em"
            }
          })}
        >
          {t("createGame.title")}
        </h1>

        <div css={sectionStyle}>
          <label css={(theme) => ({ display: "block", marginBottom: theme.gridUnit * 2, fontWeight: "bold" })}>
            {t("createGame.gameTypeLabel")}
          </label>
          <RadioButtonGroup>
            {gameTypes.map(({ labelKey, value }) => (
              <RadioButton
                key={value}
                label={t(labelKey)}
                id={`gameType-${value}`}
                checked={gameType === value}
                value={value}
                name="gameType"
                onChange={(e: React.SyntheticEvent<HTMLInputElement>) => {
                  setGameType(e.currentTarget.value as unknown as GameType);
                }}
              />
            ))}
          </RadioButtonGroup>
        </div>

        <div css={sectionStyle}>
          <label css={(theme) => ({ display: "block", marginBottom: theme.gridUnit * 2, fontWeight: "bold" })}>
            {t("createGame.startingSideLabel")}
          </label>
          <RadioButtonGroup>
            {startingSides.map(({ labelKey, value }) => (
              <RadioButton
                key={value}
                label={t(labelKey)}
                id={`startingSide-${value}`}
                checked={startingSide === value}
                value={value}
                name="startingSide"
                onChange={(e: React.SyntheticEvent<HTMLInputElement>) => {
                  setStartingSide(+e.currentTarget.value as unknown as Side);
                }}
              />
            ))}
          </RadioButtonGroup>
        </div>

        <div
          css={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.gridUnit * 3,
            "@media (min-width: 480px)": {
              flexDirection: "row"
            }
          })}
        >
          <button
            css={(theme) => ({
              ...theme.primaryButton,
              flex: 1,
              padding: theme.gridUnit * 4,
              fontSize: theme.gridUnit * 4,
              "@media (min-width: 480px)": {
                padding: theme.gridUnit * 3
              }
            })}
            onClick={handleCreateGame}
            disabled={creating}
          >
            {creating ? t("createGame.creating") : t("createGame.create")}
          </button>
          <button
            css={(theme) => ({
              ...theme.secondaryButton,
              flex: 1,
              padding: theme.gridUnit * 4,
              fontSize: theme.gridUnit * 4,
              "@media (min-width: 480px)": {
                padding: theme.gridUnit * 3
              }
            })}
            onClick={() => navigate("/multiplayer")}
            disabled={creating}
          >
            {t("common.cancel")}
          </button>
        </div>

        <div css={infoBoxStyle}>
          <p css={(theme) => ({ margin: 0, marginBottom: theme.gridUnit * 2 })}>
            <strong>{t("createGame.howItWorks")}</strong>
          </p>
          <ol css={(theme) => ({ margin: 0, paddingLeft: theme.gridUnit * 5 })}>
            <li>{t("createGame.step1")}</li>
            <li>{t("createGame.step2")}</li>
            <li>{t("createGame.step3")}</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default CreateGame;
